// ─── Helpers ────────────────────────────────────────────────────────────────

function toPascalCase(str) {
  return str.replace(/(^\w|[-_\s]\w)/g, m => m.replace(/[-_\s]/, '').toUpperCase());
}
function toCamelCase(str) {
  const p = toPascalCase(str);
  return p.charAt(0).toLowerCase() + p.slice(1);
}
function toSnakeCase(str) {
  return str.replace(/([A-Z])/g, '_$1').replace(/^_/, '').replace(/[-\s]/g, '_').toLowerCase();
}
// function toKebabCase(str) {
//   return toSnakeCase(str).replace(/_/g, '-');
// }

// function inferType(value) {
//   if (value === null) return 'null';
//   if (Array.isArray(value)) return 'array';
//   return typeof value;
// }

// ─── YAML serializer (tiny, no deps) ────────────────────────────────────────

export function jsonToYaml(input) {
  const obj = JSON.parse(input);
  return serializeYaml(obj, 0);
}

function serializeYaml(val, indent) {
  const pad = ' '.repeat(indent);
  if (val === null) return 'null';
  if (typeof val === 'boolean' || typeof val === 'number') return String(val);
  if (typeof val === 'string') {
    if (/[\n:{}[\],&*#?|<>=!%@`]/.test(val) || /^\s|\s$/.test(val) || val === '')
      return `"${val.replace(/\\/g,'\\\\').replace(/"/g,'\\"').replace(/\n/g,'\\n')}"`;
    return val;
  }
  if (Array.isArray(val)) {
    if (val.length === 0) return '[]';
    return val.map(item => {
      const s = serializeYaml(item, indent + 2);
      if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
        const lines = s.split('\n');
        return `${pad}- ${lines[0]}\n${lines.slice(1).join('\n')}`;
      }
      return `${pad}- ${s}`;
    }).join('\n');
  }
  if (typeof val === 'object') {
    const keys = Object.keys(val);
    if (keys.length === 0) return '{}';
    return keys.map(k => {
      const v = val[k];
      const ks = /[:\s#]/.test(k) ? `"${k}"` : k;
      if (typeof v === 'object' && v !== null) {
        const s = serializeYaml(v, indent + 2);
        if (Array.isArray(v) && v.length > 0 && typeof v[0] !== 'object') {
          return `${pad}${ks}:\n${s}`;
        }
        return `${pad}${ks}:\n${s}`;
      }
      return `${pad}${ks}: ${serializeYaml(v, indent + 2)}`;
    }).join('\n');
  }
  return String(val);
}

// ─── JSON → TypeScript ───────────────────────────────────────────────────────

export function jsonToTypeScript(input) {
  const obj = JSON.parse(input);
  const interfaces = {};
  function buildInterface(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => {
      let t = getTsType(k, v, interfaces);
      return `  ${k}: ${t};`;
    });
    interfaces[name] = `interface ${name} {\n${fields.join('\n')}\n}`;
  }
  function getTsType(key, val, ifaces) {
    if (val === null) return 'null';
    if (Array.isArray(val)) {
      if (val.length === 0) return 'any[]';
      const inner = getTsType(key + 'Item', val[0], ifaces);
      return `${inner}[]`;
    }
    if (typeof val === 'object') {
      const name = toPascalCase(key);
      buildInterface(name, val);
      return name;
    }
    if (typeof val === 'number') return Number.isInteger(val) ? 'number' : 'number';
    return typeof val;
  }
  buildInterface('Root', obj);
  return Object.values(interfaces).reverse().join('\n\n');
}

// ─── JSON → C# ───────────────────────────────────────────────────────────────

export function jsonToCSharp(input) {
  const obj = JSON.parse(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const props = Object.entries(o).map(([k, v]) => {
      const t = getCsType(k, v);
      return `    public ${t} ${toPascalCase(k)} { get; set; }`;
    });
    classes[name] = `public class ${name}\n{\n${props.join('\n')}\n}`;
  }
  function getCsType(key, val) {
    if (val === null) return 'object?';
    if (Array.isArray(val)) {
      if (val.length === 0) return 'List<object>';
      return `List<${getCsType(key + 'Item', val[0])}>`;
    }
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'int' : 'double';
    return 'string';
  }
  buildClass('Root', obj);
  return Object.values(classes).reverse().join('\n\n');
}

// ─── JSON → CSV ───────────────────────────────────────────────────────────────

export function jsonToCsv(input) {
  const obj = JSON.parse(input);
  const arr = Array.isArray(obj) ? obj : [obj];
  if (arr.length === 0) return '';
  const headers = [...new Set(arr.flatMap(r => Object.keys(r)))];
  const escape = v => {
    const s = v == null ? '' : String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = arr.map(r => headers.map(h => escape(r[h])).join(','));
  return [headers.join(','), ...rows].join('\n');
}

// ─── JSON → Swift ─────────────────────────────────────────────────────────────

export function jsonToSwift(input) {
  const obj = JSON.parse(input);
  const structs = {};
  function buildStruct(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const props = Object.entries(o).map(([k, v]) => {
      const t = getSwiftType(k, v);
      return `    let ${toCamelCase(k)}: ${t}`;
    });
    structs[name] = `struct ${name}: Codable {\n${props.join('\n')}\n}`;
  }
  function getSwiftType(key, val) {
    if (val === null) return 'Any?';
    if (Array.isArray(val)) {
      if (val.length === 0) return '[Any]';
      return `[${getSwiftType(key + 'Item', val[0])}]`;
    }
    if (typeof val === 'object') { buildStruct(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'Bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'Int' : 'Double';
    return 'String';
  }
  buildStruct('Root', obj);
  return Object.values(structs).reverse().join('\n\n');
}

// ─── JSON → Go ────────────────────────────────────────────────────────────────

export function jsonToGo(input) {
  const obj = JSON.parse(input);
  const structs = {};
  function buildStruct(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => {
      const t = getGoType(k, v);
      return `\t${toPascalCase(k)} ${t} \`json:"${k}"\``;
    });
    structs[name] = `type ${name} struct {\n${fields.join('\n')}\n}`;
  }
  function getGoType(key, val) {
    if (val === null) return 'interface{}';
    if (Array.isArray(val)) {
      if (val.length === 0) return '[]interface{}';
      return `[]${getGoType(key + 'Item', val[0])}`;
    }
    if (typeof val === 'object') { buildStruct(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'int' : 'float64';
    return 'string';
  }
  buildStruct('Root', obj);
  return `package main\n\n` + Object.values(structs).reverse().join('\n\n');
}

// ─── JSON → TOML ─────────────────────────────────────────────────────────────

export function jsonToToml(input) {
  const obj = JSON.parse(input);
  return serializeToml(obj, '');
}

function serializeToml(obj, prefix) {
  let simple = '';
  let complex = '';
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v === null || typeof v === 'boolean' || typeof v === 'number') {
      simple += `${k} = ${JSON.stringify(v)}\n`;
    } else if (typeof v === 'string') {
      simple += `${k} = ${JSON.stringify(v)}\n`;
    } else if (Array.isArray(v)) {
      if (v.length === 0 || typeof v[0] !== 'object') {
        simple += `${k} = [${v.map(i => JSON.stringify(i)).join(', ')}]\n`;
      } else {
        // eslint-disable-next-line no-loop-func
        v.forEach((item, index) => {
          const itemComplex = `\n[[${key}]]\n${serializeToml(item, '')}`;
          complex += itemComplex;
        });
      }
    } else if (typeof v === 'object') {
      complex += `\n[${key}]\n${serializeToml(v, key)}`;
    }
  }
  return simple + complex;
}

// ─── JSON → Haskell ───────────────────────────────────────────────────────────

export function jsonToHaskell(input) {
  const obj = JSON.parse(input);
  const modules = {};
  function buildData(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => {
      const t = getHsType(k, v);
      return `  , ${toCamelCase(k)} :: ${t}`;
    });
    const first = fields[0] ? fields[0].replace(/^ {2},/, '  {') : '  {}';
    const rest = fields.slice(1);
    modules[name] = `data ${name} = ${name}\n${first}\n${rest.join('\n')}\n  } deriving (Show, Eq)`;
  }
  function getHsType(key, val) {
    if (val === null) return 'Maybe ()';
    if (Array.isArray(val)) { return val.length === 0 ? '[()]' : `[${getHsType(key, val[0])}]`; }
    if (typeof val === 'object') { buildData(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'Bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'Int' : 'Double';
    return 'Text';
  }
  buildData('Root', obj);
  return `module Root where\n\nimport Data.Text (Text)\n\n` + Object.values(modules).reverse().join('\n\n');
}

// ─── JSON → C++ ───────────────────────────────────────────────────────────────

export function jsonToCpp(input) {
  const obj = JSON.parse(input);
  const structs = {};
  function buildStruct(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => {
      const t = getCppType(k, v);
      return `    ${t} ${toSnakeCase(k)};`;
    });
    structs[name] = `struct ${name} {\n${fields.join('\n')}\n};`;
  }
  function getCppType(key, val) {
    if (val === null) return 'std::nullptr_t';
    if (Array.isArray(val)) { return val.length === 0 ? 'std::vector<std::any>' : `std::vector<${getCppType(key, val[0])}>`; }
    if (typeof val === 'object') { buildStruct(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'int' : 'double';
    return 'std::string';
  }
  buildStruct('Root', obj);
  return `#include <string>\n#include <vector>\n#include <any>\n\n` + Object.values(structs).reverse().join('\n\n');
}

// ─── JSON Diff / Comparator ───────────────────────────────────────────────────

export function jsonCompare(input1, input2) {
  const a = JSON.parse(input1);
  const b = JSON.parse(input2);
  const diffs = [];
  function compare(x, y, path) {
    if (JSON.stringify(x) === JSON.stringify(y)) return;
    if (typeof x !== typeof y || Array.isArray(x) !== Array.isArray(y)) {
      diffs.push({ path, left: x, right: y, type: 'type_mismatch' });
      return;
    }
    if (typeof x !== 'object' || x === null) {
      diffs.push({ path, left: x, right: y, type: 'value_changed' });
      return;
    }
    const keys = new Set([...Object.keys(x), ...Object.keys(y)]);
    for (const k of keys) {
      const p = path ? `${path}.${k}` : k;
      if (!(k in x)) diffs.push({ path: p, left: undefined, right: y[k], type: 'added' });
      else if (!(k in y)) diffs.push({ path: p, left: x[k], right: undefined, type: 'removed' });
      else compare(x[k], y[k], p);
    }
  }
  compare(a, b, '');
  if (diffs.length === 0) return '✅ 两个 JSON 完全相同';
  return diffs.map(d => {
    if (d.type === 'added') return `+ ${d.path}: ${JSON.stringify(d.right)}`;
    if (d.type === 'removed') return `- ${d.path}: ${JSON.stringify(d.left)}`;
    return `~ ${d.path}: ${JSON.stringify(d.left)} → ${JSON.stringify(d.right)}`;
  }).join('\n');
}

// ─── TOML → JSON ─────────────────────────────────────────────────────────────

export function tomlToJson(input) {
  // Minimal TOML parser for flat/nested keys and simple arrays
  const lines = input.split('\n');
  const root = {};
  let current = root;
  // let currentPath = [];

  for (let line of lines) {
    line = line.trim();
    if (!line || line.startsWith('#')) continue;
    if (line.startsWith('[') && line.endsWith(']') && !line.startsWith('[[')) {
      const path = line.slice(1, -1).trim().split('.');
      // currentPath = path;
      current = root;
      for (const p of path) {
        if (!(p in current)) current[p] = {};
        current = current[p];
      }
    } else if (line.includes('=')) {
      const eqIdx = line.indexOf('=');
      const k = line.slice(0, eqIdx).trim();
      let v = line.slice(eqIdx + 1).trim();
      // parse value
      let parsed;
      if (v === 'true') parsed = true;
      else if (v === 'false') parsed = false;
      else if (v.startsWith('"') && v.endsWith('"')) parsed = JSON.parse(v);
      else if (v.startsWith('[') && v.endsWith(']')) {
        try { parsed = JSON.parse(v.replace(/'/g, '"')); } catch { parsed = v; }
      } else if (!isNaN(Number(v))) parsed = Number(v);
      else parsed = v;
      current[k] = parsed;
    }
  }
  return JSON.stringify(root, null, 2);
}

// ─── JSON → Ruby ─────────────────────────────────────────────────────────────

export function jsonToRuby(input) {
  const obj = JSON.parse(input);
  const modules = {};
  function buildModule(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const attrs = Object.keys(o).map(k => `    attr_accessor :${toSnakeCase(k)}`);
    const init = Object.keys(o).map(k => `      @${toSnakeCase(k)} = data[:${toSnakeCase(k)}]`);
    modules[name] = `  class ${name}\n${attrs.join('\n')}\n\n    def initialize(data = {})\n${init.join('\n')}\n    end\n  end`;
  }
  buildModule('Root', obj);
  return `module GeneratedModels\n${Object.values(modules).reverse().join('\n\n')}\nend`;
}

// ─── JSON → Rust ─────────────────────────────────────────────────────────────

export function jsonToRust(input) {
  const obj = JSON.parse(input);
  const structs = {};
  function buildStruct(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => {
      const t = getRustType(k, v);
      return `    pub ${toSnakeCase(k)}: ${t},`;
    });
    structs[name] = `#[derive(Debug, Serialize, Deserialize)]\npub struct ${name} {\n${fields.join('\n')}\n}`;
  }
  function getRustType(key, val) {
    if (val === null) return 'Option<serde_json::Value>';
    if (Array.isArray(val)) { return val.length === 0 ? 'Vec<serde_json::Value>' : `Vec<${getRustType(key, val[0])}>`; }
    if (typeof val === 'object') { buildStruct(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'i64' : 'f64';
    return 'String';
  }
  buildStruct('Root', obj);
  return `use serde::{Deserialize, Serialize};\n\n` + Object.values(structs).reverse().join('\n\n');
}

// ─── JSON Minifier ────────────────────────────────────────────────────────────

export function jsonMinify(input) {
  return JSON.stringify(JSON.parse(input));
}

// ─── JSON → Pike ─────────────────────────────────────────────────────────────

export function jsonToPike(input) {
  const obj = JSON.parse(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => {
      const t = getPikeType(k, v);
      return `  ${t} ${k};`;
    });
    classes[name] = `class ${name} {\n${fields.join('\n')}\n}`;
  }
  function getPikeType(key, val) {
    if (val === null) return 'mixed';
    if (Array.isArray(val)) return 'array';
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'int';
    if (typeof val === 'number') return Number.isInteger(val) ? 'int' : 'float';
    return 'string';
  }
  buildClass('Root', obj);
  return Object.values(classes).reverse().join('\n\n');
}

// ─── JSON → SQL Schema ────────────────────────────────────────────────────────

export function jsonToSqlSchema(input) {
  const obj = JSON.parse(input);
  const arr = Array.isArray(obj) ? obj : [obj];
  const sample = arr[0] || {};
  const cols = Object.entries(sample).map(([k, v]) => {
    let t = 'TEXT';
    if (typeof v === 'boolean') t = 'BOOLEAN';
    else if (typeof v === 'number') t = Number.isInteger(v) ? 'INTEGER' : 'FLOAT';
    else if (typeof v === 'object' && v !== null) t = 'JSONB';
    return `  ${toSnakeCase(k)} ${t}`;
  });
  return `CREATE TABLE root (\n  id SERIAL PRIMARY KEY,\n${cols.join(',\n')}\n);`;
}

// ─── JSON Formatter ───────────────────────────────────────────────────────────

export function jsonFormat(input) {
  return JSON.stringify(JSON.parse(input), null, 2);
}

// ─── JSON → XML ───────────────────────────────────────────────────────────────

export function jsonToXml(input) {
  const obj = JSON.parse(input);
  function toXml(val, tag) {
    const open = `<${tag}>`;
    const close = `</${tag}>`;
    if (val === null) return `${open}${close}`;
    if (Array.isArray(val)) return val.map(item => toXml(item, tag.replace(/s$/, ''))).join('\n');
    if (typeof val === 'object') {
      const inner = Object.entries(val).map(([k, v]) => toXml(v, k)).join('\n');
      return `${open}\n${inner.split('\n').map(l => '  ' + l).join('\n')}\n${close}`;
    }
    return `${open}${val}${close}`;
  }
  return `<?xml version="1.0" encoding="UTF-8"?>\n${toXml(obj, 'root')}`;
}

// ─── JSON → Zod Schema ────────────────────────────────────────────────────────

export function jsonToZod(input) {
  const obj = JSON.parse(input);
  const schemas = {};
  function buildSchema(name, val) {
    if (typeof val !== 'object' || val === null || Array.isArray(val)) return zodType(name, val);
    const fields = Object.entries(val).map(([k, v]) => `  ${k}: ${buildSchema(k, v)},`);
    schemas[name] = `const ${toCamelCase(name)}Schema = z.object({\n${fields.join('\n')}\n});`;
    return `${toCamelCase(name)}Schema`;
  }
  function zodType(key, val) {
    if (val === null) return 'z.null()';
    if (Array.isArray(val)) return val.length === 0 ? 'z.array(z.any())' : `z.array(${zodType(key, val[0])})`;
    if (typeof val === 'object') { buildSchema(toPascalCase(key), val); return `${toCamelCase(key)}Schema`; }
    if (typeof val === 'boolean') return 'z.boolean()';
    if (typeof val === 'number') return Number.isInteger(val) ? 'z.number().int()' : 'z.number()';
    return 'z.string()';
  }
  buildSchema('Root', obj);
  return `import { z } from 'zod';\n\n` + Object.values(schemas).reverse().join('\n\n') + `\n\nexport type Root = z.infer<typeof rootSchema>;`;
}

// ─── JSON → Dart ─────────────────────────────────────────────────────────────

export function jsonToDart(input) {
  const obj = JSON.parse(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `  final ${getDartType(k, v)} ${toCamelCase(k)};`);
    const params = Object.keys(o).map(k => `    required this.${toCamelCase(k)},`);
    classes[name] = `class ${name} {\n${fields.join('\n')}\n\n  const ${name}({${params.join('\n')}\n  });\n\n  factory ${name}.fromJson(Map<String, dynamic> json) => ${name}(\n${Object.entries(o).map(([k, v]) => `    ${toCamelCase(k)}: json['${k}'],`).join('\n')}\n  );\n}`;
  }
  function getDartType(key, val) {
    if (val === null) return 'dynamic';
    if (Array.isArray(val)) return val.length === 0 ? 'List<dynamic>' : `List<${getDartType(key, val[0])}>`;
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'int' : 'double';
    return 'String';
  }
  buildClass('Root', obj);
  return Object.values(classes).reverse().join('\n\n');
}

// ─── JSON → PHP ───────────────────────────────────────────────────────────────

export function jsonToPhp(input) {
  const obj = JSON.parse(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const props = Object.entries(o).map(([k, v]) => {
      const phpType = getPhpType(k, v);
      return `    public ${phpType} $${toCamelCase(k)};`;
    });
    classes[name] = `class ${name}\n{\n${props.join('\n')}\n}`;
  }
  function getPhpType(key, val) {
    if (val === null) return '?mixed';
    if (Array.isArray(val)) return 'array';
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'int' : 'float';
    return 'string';
  }
  buildClass('Root', obj);
  return `<?php\n\n` + Object.values(classes).reverse().join('\n\n');
}

// ─── JSON Merger ─────────────────────────────────────────────────────────────

export function jsonMerge(input1, input2) {
  const a = JSON.parse(input1);
  const b = JSON.parse(input2);
  function deepMerge(x, y) {
    if (Array.isArray(x) && Array.isArray(y)) return [...x, ...y];
    if (typeof x === 'object' && x !== null && typeof y === 'object' && y !== null) {
      const result = { ...x };
      for (const [k, v] of Object.entries(y)) {
        result[k] = k in result ? deepMerge(result[k], v) : v;
      }
      return result;
    }
    return y;
  }
  return JSON.stringify(deepMerge(a, b), null, 2);
}

// ─── JSON → Objective-C ───────────────────────────────────────────────────────

export function jsonToObjC(input) {
  const obj = JSON.parse(input);
  const interfaces = {};
  function buildInterface(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const props = Object.entries(o).map(([k, v]) => {
      const t = getObjCType(k, v);
      return `@property (nonatomic, strong) ${t} *${toCamelCase(k)};`;
    });
    interfaces[name] = `@interface ${name} : NSObject\n${props.join('\n')}\n@end`;
  }
  function getObjCType(key, val) {
    if (val === null) return 'id';
    if (Array.isArray(val)) return 'NSArray';
    if (typeof val === 'object') { buildInterface(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'NSNumber';
    if (typeof val === 'number') return 'NSNumber';
    return 'NSString';
  }
  buildInterface('Root', obj);
  return `#import <Foundation/Foundation.h>\n\n` + Object.values(interfaces).reverse().join('\n\n');
}

// ─── JSON → Elm ───────────────────────────────────────────────────────────────

export function jsonToElm(input) {
  const obj = JSON.parse(input);
  const types = {};
  function buildType(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `    , ${toCamelCase(k)} : ${getElmType(k, v)}`);
    const first = fields[0] ? `    { ${fields[0].slice(6)}` : '    {}';
    types[name] = `type alias ${name} =\n${first}\n${fields.slice(1).join('\n')}\n    }`;
  }
  function getElmType(key, val) {
    if (val === null) return 'Maybe ()';
    if (Array.isArray(val)) return val.length === 0 ? 'List ()' : `List ${getElmType(key, val[0])}`;
    if (typeof val === 'object') { buildType(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'Bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'Int' : 'Float';
    return 'String';
  }
  buildType('Root', obj);
  return `module Root exposing (..)\n\n` + Object.values(types).reverse().join('\n\n');
}

// ─── JSON Generator ───────────────────────────────────────────────────────────

export function jsonGenerate(schemaInput) {
  // Generate sample JSON from a JSON Schema
  try {
    const schema = JSON.parse(schemaInput);
    return JSON.stringify(generateFromSchema(schema), null, 2);
  } catch {
    return JSON.stringify({
      id: 1,
      name: "示例名称",
      email: "example@email.com",
      age: 25,
      active: true,
      tags: ["tag1", "tag2"],
      address: {
        street: "123 Main St",
        city: "城市",
        country: "中国"
      }
    }, null, 2);
  }
}

function generateFromSchema(schema) {
  if (!schema || !schema.type) return {};
  switch (schema.type) {
    case 'object': {
      const obj = {};
      for (const [k, v] of Object.entries(schema.properties || {})) {
        obj[k] = generateFromSchema(v);
      }
      return obj;
    }
    case 'array': return [generateFromSchema(schema.items || {})];
    case 'string': return schema.example || schema.default || 'string';
    case 'number': case 'integer': return schema.example || schema.default || 0;
    case 'boolean': return schema.example || schema.default || false;
    default: return null;
  }
}

// ─── JSON Sarcastic ───────────────────────────────────────────────────────────

export function jsonToSarcastic(input) {
  const obj = JSON.parse(input);
  function sarcastify(val) {
    if (typeof val === 'string') {
      return val.split('').map((c, i) => i % 2 === 0 ? c.toLowerCase() : c.toUpperCase()).join('');
    }
    if (Array.isArray(val)) return val.map(sarcastify);
    if (typeof val === 'object' && val !== null) {
      const r = {};
      for (const [k, v] of Object.entries(val)) r[k] = sarcastify(v);
      return r;
    }
    return val;
  }
  return JSON.stringify(sarcastify(obj), null, 2);
}

// ─── JSON → Crystal ───────────────────────────────────────────────────────────

export function jsonToCrystal(input) {
  const obj = JSON.parse(input);
  const structs = {};
  function buildStruct(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => {
      return `  property ${toSnakeCase(k)} : ${getCrystalType(k, v)}`;
    });
    structs[name] = `struct ${name}\n${fields.join('\n')}\nend`;
  }
  function getCrystalType(key, val) {
    if (val === null) return 'Nil';
    if (Array.isArray(val)) return val.length === 0 ? 'Array(JSON::Any)' : `Array(${getCrystalType(key, val[0])})`;
    if (typeof val === 'object') { buildStruct(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'Bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'Int32' : 'Float64';
    return 'String';
  }
  buildStruct('Root', obj);
  return Object.values(structs).reverse().join('\n\n');
}

// ─── JSON → Java Bean ─────────────────────────────────────────────────────────

export function jsonToJava(input) {
  const obj = JSON.parse(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `    private ${getJavaType(k, v)} ${toCamelCase(k)};`);
    const getters = Object.entries(o).map(([k, v]) => {
      const jt = getJavaType(k, v);
      const cc = toCamelCase(k);
      const pc = toPascalCase(k);
      return `    public ${jt} get${pc}() { return ${cc}; }\n    public void set${pc}(${jt} ${cc}) { this.${cc} = ${cc}; }`;
    });
    classes[name] = `public class ${name} {\n${fields.join('\n')}\n\n${getters.join('\n\n')}\n}`;
  }
  function getJavaType(key, val) {
    if (val === null) return 'Object';
    if (Array.isArray(val)) return val.length === 0 ? 'List<Object>' : `List<${getJavaType(key, val[0])}>`;
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'Boolean';
    if (typeof val === 'number') return Number.isInteger(val) ? 'Integer' : 'Double';
    return 'String';
  }
  buildClass('Root', obj);
  return `import java.util.List;\n\n` + Object.values(classes).reverse().join('\n\n');
}

// ─── JSON → JSON Schema ───────────────────────────────────────────────────────

export function jsonToJsonSchema(input) {
  const obj = JSON.parse(input);
  function toSchema(val) {
    if (val === null) return { type: 'null' };
    if (Array.isArray(val)) {
      return { type: 'array', items: val.length > 0 ? toSchema(val[0]) : {} };
    }
    if (typeof val === 'object') {
      const props = {};
      for (const [k, v] of Object.entries(val)) props[k] = toSchema(v);
      return { type: 'object', properties: props, required: Object.keys(val) };
    }
    return { type: typeof val };
  }
  const schema = {
    $schema: 'http://json-schema.org/draft-07/schema#',
    ...toSchema(obj)
  };
  return JSON.stringify(schema, null, 2);
}

// ─── JSON → Kotlin ────────────────────────────────────────────────────────────

export function jsonToKotlin(input) {
  const obj = JSON.parse(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const params = Object.entries(o).map(([k, v]) => `    val ${toCamelCase(k)}: ${getKotlinType(k, v)}`);
    classes[name] = `data class ${name}(\n${params.join(',\n')}\n)`;
  }
  function getKotlinType(key, val) {
    if (val === null) return 'Any?';
    if (Array.isArray(val)) return val.length === 0 ? 'List<Any>' : `List<${getKotlinType(key, val[0])}>`;
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'Boolean';
    if (typeof val === 'number') return Number.isInteger(val) ? 'Int' : 'Double';
    return 'String';
  }
  buildClass('Root', obj);
  return Object.values(classes).reverse().join('\n\n');
}

// ─── JSON → Scala ─────────────────────────────────────────────────────────────

export function jsonToScala(input) {
  const obj = JSON.parse(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const params = Object.entries(o).map(([k, v]) => `  ${toCamelCase(k)}: ${getScalaType(k, v)}`);
    classes[name] = `case class ${name}(\n${params.join(',\n')}\n)`;
  }
  function getScalaType(key, val) {
    if (val === null) return 'Option[Any]';
    if (Array.isArray(val)) return val.length === 0 ? 'List[Any]' : `List[${getScalaType(key, val[0])}]`;
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'Boolean';
    if (typeof val === 'number') return Number.isInteger(val) ? 'Int' : 'Double';
    return 'String';
  }
  buildClass('Root', obj);
  return Object.values(classes).reverse().join('\n\n');
}

// ─── JSON → Python ────────────────────────────────────────────────────────────

export function jsonToPython(input) {
  const obj = JSON.parse(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `    ${toSnakeCase(k)}: ${getPyType(k, v)}`);
    classes[name] = `@dataclass\nclass ${name}:\n${fields.join('\n')}`;
  }
  function getPyType(key, val) {
    if (val === null) return 'Optional[Any]';
    if (Array.isArray(val)) return val.length === 0 ? 'List[Any]' : `List[${getPyType(key, val[0])}]`;
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'int' : 'float';
    return 'str';
  }
  buildClass('Root', obj);
  return `from dataclasses import dataclass\nfrom typing import Any, List, Optional\n\n` + Object.values(classes).reverse().join('\n\n');
}

// ─── JSON → IO-TS ─────────────────────────────────────────────────────────────

export function jsonToIoTs(input) {
  const obj = JSON.parse(input);
  const codecs = {};
  function buildCodec(name, val) {
    if (typeof val !== 'object' || val === null || Array.isArray(val)) return ioTsType(name, val);
    const fields = Object.entries(val).map(([k, v]) => `  ${k}: ${buildCodec(k, v)},`);
    codecs[name] = `const ${toCamelCase(name)}Codec = t.type({\n${fields.join('\n')}\n});`;
    return `${toCamelCase(name)}Codec`;
  }
  function ioTsType(key, val) {
    if (val === null) return 't.null';
    if (Array.isArray(val)) return val.length === 0 ? 't.array(t.unknown)' : `t.array(${ioTsType(key, val[0])})`;
    if (typeof val === 'object') { buildCodec(toPascalCase(key), val); return `${toCamelCase(key)}Codec`; }
    if (typeof val === 'boolean') return 't.boolean';
    if (typeof val === 'number') return 't.number';
    return 't.string';
  }
  buildCodec('Root', obj);
  return `import * as t from 'io-ts';\n\n` + Object.values(codecs).reverse().join('\n\n') + `\n\nexport type Root = t.TypeOf<typeof rootCodec>;`;
}

// ─── SQL Schema → JSON ────────────────────────────────────────────────────────

export function sqlSchemaToJson(input) {
  const cols = [];
  const regex = /(\w+)\s+(TEXT|VARCHAR|INT(?:EGER)?|FLOAT|DOUBLE|BOOLEAN|BOOL|JSONB|JSON|SERIAL|BIGINT|SMALLINT|REAL|NUMERIC|DECIMAL)\b/gi;
  let m;
  while ((m = regex.exec(input)) !== null) {
    const name = m[1].toUpperCase();
    if (['CREATE', 'TABLE', 'PRIMARY', 'KEY', 'NOT', 'NULL', 'DEFAULT', 'REFERENCES', 'UNIQUE', 'INDEX', 'ID'].includes(name)) continue;
    const type = m[2].toUpperCase();
    let val;
    if (['INT', 'INTEGER', 'BIGINT', 'SMALLINT', 'SERIAL'].includes(type)) val = 0;
    else if (['FLOAT', 'DOUBLE', 'REAL', 'NUMERIC', 'DECIMAL'].includes(type)) val = 0.0;
    else if (['BOOLEAN', 'BOOL'].includes(type)) val = false;
    else if (['JSONB', 'JSON'].includes(type)) val = {};
    else val = '';
    cols.push([toCamelCase(m[1]), val]);
  }
  return JSON.stringify(Object.fromEntries(cols), null, 2);
}

// ─── JSON → BigQuery Schema ───────────────────────────────────────────────────

export function jsonToBigQuery(input) {
  const obj = JSON.parse(input);
  function toBqFields(o) {
    return Object.entries(o).map(([k, v]) => {
      if (Array.isArray(v)) {
        const inner = v.length > 0 && typeof v[0] === 'object' ? { type: 'RECORD', fields: toBqFields(v[0]) } : { type: getBqType(v[0]) };
        return { name: k, mode: 'REPEATED', ...inner };
      }
      if (typeof v === 'object' && v !== null) {
        return { name: k, type: 'RECORD', mode: 'NULLABLE', fields: toBqFields(v) };
      }
      return { name: k, type: getBqType(v), mode: 'NULLABLE' };
    });
  }
  function getBqType(val) {
    if (val === null) return 'STRING';
    if (typeof val === 'boolean') return 'BOOL';
    if (typeof val === 'number') return Number.isInteger(val) ? 'INT64' : 'FLOAT64';
    return 'STRING';
  }
  return JSON.stringify(toBqFields(obj), null, 2);
}

// ─── JSON Path ────────────────────────────────────────────────────────────────

export function jsonPath(input, pathExpr) {
  const obj = JSON.parse(input);
  const path = pathExpr.replace(/^\$\.?/, '').split(/[.[\]]+/).filter(Boolean);
  let current = obj;
  for (const seg of path) {
    if (current == null) { current = undefined; break; }
    current = current[isNaN(Number(seg)) ? seg : Number(seg)];
  }
  return JSON.stringify(current, null, 2);
}
